import { parsePhoneNumber } from "libphonenumber-js";

export interface PhoneProcessingResult {
  isValid: boolean;
  e164: string | null;
  national: string | null;
}

const EMPTY_RESULT: PhoneProcessingResult = {
  isValid: false,
  e164: null,
  national: null,
};

/**
 * Parses, validates, and normalizes a phone number.
 *
 * @param inputNumber - The raw phone number input (can be any format).
 * @param defaultCountryCode - The default country code (e.g., "US", "GB").
 * @returns {{isValid: boolean, e164: string | null, national: string | null}}
 */
export function processPhoneNumber(
  inputNumber: string,
  defaultCountryCode: string,
): PhoneProcessingResult {
  if (!inputNumber || !inputNumber.trim()) {
    return { ...EMPTY_RESULT };
  }

  try {
    const phone = parsePhoneNumber(inputNumber, defaultCountryCode as any);

    if (!phone || !phone.isValid()) {
      return { ...EMPTY_RESULT };
    }

    return {
      isValid: true,
      e164: phone.format("E.164"),
      national: phone.format("NATIONAL"),
    };
  } catch {
    return { ...EMPTY_RESULT };
  }
}

/**
 * Lazy variant that dynamically loads libphonenumber-js.
 * Useful when you want to avoid pulling the library into the initial bundle.
 */
export async function processPhoneNumberLazy(
  inputNumber: string,
  defaultCountryCode: string,
): Promise<PhoneProcessingResult> {
  if (!inputNumber || !inputNumber.trim()) {
    return { ...EMPTY_RESULT };
  }

  try {
    const { parsePhoneNumber: parseDynamic } = await import("libphonenumber-js");
    const phone = parseDynamic(inputNumber, defaultCountryCode as any);

    if (!phone || !phone.isValid()) {
      return { ...EMPTY_RESULT };
    }

    return {
      isValid: true,
      e164: phone.format("E.164"),
      national: phone.format("NATIONAL"),
    };
  } catch {
    return { ...EMPTY_RESULT };
  }
}


